import React, { useState, useEffect } from 'react';
import { ShoppingBag, Heart, Search, Menu, X, ChevronDown, User } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from './ui/dropdown-menu';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';

export const Header = ({ onCartClick, onWishlistClick, cartCount }) => {
  const navigate = useNavigate();
  const { customer, isAuthenticated, logout } = useCustomerAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  const handleCategoryClick = (category) => {
    navigate(`/search?category=${encodeURIComponent(category)}`);
    setIsMobileMenuOpen(false);
  };

  const catalogCategories = [
    'Chanderi Collection',
    'Muslin Suits',
    'Organza Suits',
    'Silk Collection',
    'Coords',
    'Cotton Suits',
    'Party Wear',
    'Georgette Collection',
    'Pakistani Suits',
    'Tissue Collection',
    'Velvet Collection',
    'Handpainted Collection'
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
      }`}
    >
      {/* Top Bar */}
      <div className="bg-[#6B4E37] text-[#FAF9F7] text-center py-2 px-4">
        <p className="text-sm font-montserrat tracking-wide">
          FREE SHIPPING ON ORDERS ABOVE ₹2,999 | COD AVAILABLE
        </p>
      </div>

      {/* Main Header */}
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/">
              <h1 className="font-seasons !font-semibold !text-3xl text-[#2C2C2C] hover:text-[#C4B5A0] transition-colors duration-200 cursor-pointer">
                Sachdeva Sons
              </h1>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            <Link
              to="/search"
              className="text-sm font-montserrat tracking-wider text-[#2C2C2C] hover:text-[#C4B5A0] transition-colors duration-200 uppercase"
            >
              New Arrivals
            </Link>

            {/* Catalog Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="text-sm font-montserrat tracking-wider text-[#2C2C2C] hover:text-[#C4B5A0] transition-colors duration-200 uppercase px-2"
                >
                  Catalog
                  <ChevronDown className="ml-1 h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56 bg-white shadow-lg">
                {catalogCategories.map((category) => (
                  <DropdownMenuItem
                    key={category}
                    onClick={() => handleCategoryClick(category)}
                    className="font-montserrat text-sm text-[#2C2C2C] hover:text-[#C4B5A0] hover:bg-[#F5F3F0] cursor-pointer"
                  >
                    {category}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Link
              to="/about"
              className="text-sm font-montserrat tracking-wider text-[#2C2C2C] hover:text-[#C4B5A0] transition-colors duration-200 uppercase"
            >
              About Us
            </Link>
            <Link
              to="/contact"
              className="text-sm font-montserrat tracking-wider text-[#2C2C2C] hover:text-[#C4B5A0] transition-colors duration-200 uppercase"
            >
              Contact
            </Link>
          </nav>

          {/* Action Icons */}
          <div className="flex items-center space-x-4">
            {/* Search */}
            <Button
              variant="ghost"
              size="icon"
              className="text-[#2C2C2C] hover:text-[#C4B5A0] transition-colors"
              onClick={() => setSearchOpen(!searchOpen)}
            >
              <Search className="h-5 w-5" />
            </Button>

            {/* User Account */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-[#2C2C2C] hover:text-[#C4B5A0] transition-colors"
                >
                  <User className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-white shadow-lg">
                {isAuthenticated ? (
                  <>
                    <div className="px-3 py-2 border-b border-[#E8E5E0]">
                      <p className="font-montserrat text-sm text-[#2C2C2C] font-medium">{customer?.name}</p>
                      <p className="font-montserrat text-xs text-[#8B7D6B] truncate">{customer?.email}</p>
                    </div>
                    <DropdownMenuItem
                      onClick={() => navigate('/account')}
                      className="font-montserrat text-sm cursor-pointer"
                    >
                      My Account
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate('/account/orders')}
                      className="font-montserrat text-sm cursor-pointer"
                    >
                      My Orders
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => { logout(); navigate('/'); }}
                      className="font-montserrat text-sm text-red-500 cursor-pointer"
                    >
                      Logout
                    </DropdownMenuItem>
                  </>
                ) : (
                  <>
                    <DropdownMenuItem
                      onClick={() => navigate('/login')}
                      className="font-montserrat text-sm cursor-pointer"
                    >
                      Sign In
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate('/login')}
                      className="font-montserrat text-sm cursor-pointer"
                    >
                      Create Account
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Wishlist */}
            <Button
              variant="ghost"
              size="icon"
              className="text-[#2C2C2C] hover:text-[#C4B5A0] transition-colors"
              onClick={onWishlistClick}
            >
              <Heart className="h-5 w-5" />
            </Button>

            {/* Cart */}
            <Button
              variant="ghost"
              size="icon"
              className="relative text-[#2C2C2C] hover:text-[#C4B5A0] transition-colors"
              onClick={onCartClick}
            >
              <ShoppingBag className="h-5 w-5" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#C4B5A0] text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-montserrat">
                  {cartCount}
                </span>
              )}
            </Button>

            {/* Mobile Menu */}
            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon" className="text-[#2C2C2C]">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] bg-[#FAF9F7]">
                <nav className="flex flex-col space-y-6 mt-8">
                  {isAuthenticated && (
                    <div className="pb-4 border-b border-[#E8E5E0]">
                      <p className="font-montserrat text-sm text-[#2C2C2C] font-medium">{customer?.name}</p>
                      <Link
                        to="/account"
                        className="text-sm font-montserrat text-[#C4B5A0]"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        View Account
                      </Link>
                    </div>
                  )}

                  <Link
                    to="/search"
                    className="text-base font-montserrat tracking-wider text-[#2C2C2C] hover:text-[#C4B5A0] transition-colors uppercase"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    New Arrivals
                  </Link>

                  {/* Catalog Section */}
                  <div>
                    <p className="text-base font-montserrat tracking-wider text-[#6B4E37] uppercase mb-3 font-medium">
                      Catalog
                    </p>
                    <div className="flex flex-col space-y-3 pl-4">
                      {catalogCategories.map((category) => (
                        <button
                          key={category}
                          onClick={() => handleCategoryClick(category)}
                          className="text-sm font-montserrat text-[#2C2C2C] hover:text-[#C4B5A0] transition-colors text-left"
                        >
                          {category}
                        </button>
                      ))}
                    </div>
                  </div>

                  <Link
                    to="/about"
                    className="text-base font-montserrat tracking-wider text-[#2C2C2C] hover:text-[#C4B5A0] transition-colors uppercase"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    About Us
                  </Link>
                  <Link
                    to="/contact"
                    className="text-base font-montserrat tracking-wider text-[#2C2C2C] hover:text-[#C4B5A0] transition-colors uppercase"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Contact
                  </Link>

                  {!isAuthenticated && (
                    <Link
                      to="/login"
                      className="text-base font-montserrat tracking-wider text-[#C4B5A0] hover:text-[#B0A090] transition-colors uppercase"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Sign In / Register
                    </Link>
                  )}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {/* Search Bar */}
        {searchOpen && (
          <div className="pb-4 animate-in slide-in-from-top duration-200">
            <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
              <div className="relative">
                <Input
                  type="search"
                  placeholder="Search for suits, sarees, lehengas..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-[#F5F3F0] border-none focus:ring-2 focus:ring-[#C4B5A0] pr-12"
                />
                <Button
                  type="submit"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 bg-[#C4B5A0] hover:bg-[#B0A090]"
                >
                  <Search className="h-4 w-4" />
                </Button>
              </div>
            </form>
          </div>
        )}
      </div>
    </header>
  );
};
